import React, { useState, useMemo, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { generateAvailabilitySlots, formatTimezoneAbbr } from '../../../helpers/timezone_utils';
import { createCall, updateCall } from '../../../api/v2/calls';
import { trackPhoneCallClick } from '../../../api/activities';
import { failToast } from '../../ui_kit/utilities/fail_toast';

import IncentiveBanner from '../incentive_banner';

import styles from './index.module.css';

const MIN_SLOTS = 3;
const MAX_SLOTS = 6;
const ONE_HOUR_MS = 3600 * 1000;

function isSlotBlocked(slot, blockedSlots) {
  if (!blockedSlots?.length) return false;
  const slotStart = new Date(slot.start_at).getTime();
  return blockedSlots.some(({ start_at }) => Math.abs(slotStart - new Date(start_at).getTime()) < ONE_HOUR_MS);
}

function makeDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatStartTime(iso, timezone) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

function formatTimezoneOffset(timezone) {
  try {
    const offsetMin = -new Date().getTimezoneOffset();
    const tzNow = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
    const localNow = new Date(new Date().toLocaleString('en-US'));
    const diffMin = Math.round((tzNow - localNow) / 60000) + offsetMin;
    const sign = diffMin >= 0 ? '+' : '-';
    const abs = Math.abs(diffMin);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    return minutes === 0 ? `GMT${sign}${hours}` : `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`;
  } catch {
    return null;
  }
}

function formatTimezoneLabel(timezone) {
  if (!timezone) return null;
  try {
    const longName = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long',
    })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    const offset = formatTimezoneOffset(timezone);
    if (longName && offset) return `${longName} (${offset})`;
    return longName || offset || null;
  } catch {
    return null;
  }
}

function getInitials(name) {
  if (!name) return 'PP';
  return name
    .trim()
    .split(' ')
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function groupSlotsByPeriod(slots, displayTimezone) {
  const morning = [];
  const afternoon = [];
  const evening = [];
  slots.forEach((slot) => {
    const hourStr = new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      hour: 'numeric',
      hour12: false,
    }).format(new Date(slot.start_at));
    const hour = parseInt(hourStr, 10) % 24;
    if (hour < 12) morning.push(slot);
    else if (hour < 17) afternoon.push(slot);
    else evening.push(slot);
  });
  return { morning, afternoon, evening };
}

export default function AvailabilityPicker({
  conversationId,
  existingCallId,
  mentorTimezone,
  mentorFirstName,
  otherUserName,
  otherUserPhoto,
  viewerTimezone,
  blockedSlots,
  incentive = null,
  submitProposedSlots,
  onCallCreated,
  onClose,
  onBack,
}) {
  const { days } = useMemo(() => generateAvailabilitySlots(mentorTimezone), [mentorTimezone]);

  const displayTimezone = viewerTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const displayName = otherUserName || mentorFirstName || 'them';

  const dayMap = useMemo(() => {
    const m = new Map();
    days.forEach((d) => m.set(d.key, d));
    return m;
  }, [days]);

  const availableKeys = useMemo(() => new Set(days.map((d) => d.key)), [days]);

  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const [selectedDate, setSelectedDate] = useState(days[0] ? new Date(days[0].key + 'T12:00:00') : null);
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const activeDayKey = selectedDate ? makeDateKey(selectedDate) : null;
  const activeDay = activeDayKey ? dayMap.get(activeDayKey) : null;

  const allSlots = useMemo(() => days.flatMap((d) => d.slots), [days]);
  const selectedSlotObjects = allSlots.filter((s) => selectedSlots.has(s.key));
  const selectedCount = selectedSlotObjects.length;
  const remaining = Math.max(0, MIN_SLOTS - selectedCount);

  const minDate = days[0] ? new Date(days[0].key + 'T12:00:00') : new Date();
  const maxDate = days[days.length - 1] ? new Date(days[days.length - 1].key + 'T12:00:00') : new Date();

  const tzLabel = formatTimezoneLabel(displayTimezone) || formatTimezoneAbbr(displayTimezone);

  const close = () => {
    if (typeof onClose === 'function') onClose();
    else if (typeof onBack === 'function') onBack();
  };

  const handleCloseClick = () => {
    if (conversationId) {
      trackPhoneCallClick('schedule_call_modal_close', { conversation_id: conversationId });
    }
    close();
  };

  const toggleSlot = (slot) => {
    if (isSlotBlocked(slot, blockedSlots)) return;
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(slot.key)) next.delete(slot.key);
      else if (next.size < MAX_SLOTS) next.add(slot.key);
      return next;
    });
  };

  const handleSubmit = () => {
    if (selectedCount < MIN_SLOTS || loading) return;
    if (conversationId) {
      trackPhoneCallClick(existingCallId ? 'schedule_call_modal_propose_new_times' : 'schedule_call_modal_send_times', {
        conversation_id: conversationId,
      });
    }
    setLoading(true);
    const proposedSlots = selectedSlotObjects.map((s) => ({ start_at: s.start_at, end_at: s.end_at }));
    let request;
    if (typeof submitProposedSlots === 'function') {
      request = submitProposedSlots(proposedSlots);
    } else if (existingCallId) {
      request = updateCall(conversationId, existingCallId, { call: { proposed_slots: proposedSlots } });
    } else {
      request = createCall(conversationId, { scheduled: true, proposed_slots: proposedSlots });
    }
    request
      .then((call) => {
        onCallCreated(call);
      })
      .catch((err) => {
        failToast(
          (err.message || 'Something went wrong') +
            ' Please try again or reach out to support@patientpartner.com if this continues.'
        );
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  };

  const selectedDayKeys = useMemo(() => {
    const keys = new Set();
    selectedSlotObjects.forEach((s) => keys.add(makeDateKey(new Date(s.start_at))));
    return keys;
  }, [selectedSlotObjects]);

  const grouped = activeDay ? groupSlotsByPeriod(activeDay.slots, displayTimezone) : null;

  const onBackdropClick = (event) => {
    if (event.target === event.currentTarget) close();
  };

  const renderSlotButton = (slot) => {
    const isSelected = selectedSlots.has(slot.key);
    const isBlocked = isSlotBlocked(slot, blockedSlots);
    const className = [
      styles.slot_chip,
      isSelected ? styles.slot_chip_selected : '',
      isBlocked ? styles.slot_chip_blocked : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <button
        key={slot.key}
        type='button'
        onClick={() => toggleSlot(slot)}
        disabled={isBlocked}
        className={className}
        title={isBlocked ? 'Already booked' : undefined}
      >
        {formatStartTime(slot.start_at, displayTimezone)}
      </button>
    );
  };

  const renderPeriodSection = (label, slots) => {
    if (!slots || slots.length === 0) return null;
    return (
      <div className={styles.period_section}>
        <h4 className={styles.period_label}>{label}</h4>
        <div className={styles.slot_chip_grid}>{slots.map(renderSlotButton)}</div>
      </div>
    );
  };

  const submitLabel = loading
    ? 'Sending…'
    : remaining > 0
      ? `Select ${remaining} more`
      : `Send Times to ${mentorFirstName || displayName.split(' ')[0]}`;

  return (
    <div className={styles.modal_overlay} onClick={onBackdropClick} role='dialog' aria-modal='true'>
      <div className={styles.modal} role='document'>
        <div className={styles.modal_header}>
          <div className={styles.header_top}>
            <div className={styles.header_user}>
              <div className={styles.avatar} aria-hidden='true'>
                {otherUserPhoto ? (
                  <img src={otherUserPhoto} alt='' draggable='false' />
                ) : (
                  <span>{getInitials(displayName)}</span>
                )}
              </div>
              <div className={styles.header_text}>
                <h2 className={styles.header_title}>Schedule a call</h2>
                <p className={styles.header_subtitle}>with {displayName}</p>
              </div>
            </div>
            <button type='button' className={styles.close_btn} onClick={handleCloseClick} aria-label='Close'>
              <svg width='14' height='14' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M3 3L13 13M13 3L3 13' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' />
              </svg>
            </button>
          </div>
          <div className={styles.info_bar}>
            <svg width='13' height='13' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <rect x='2' y='3' width='12' height='11' rx='2' stroke='#9C9287' strokeWidth='1.08' fill='none' />
              <path d='M2 7H14' stroke='#9C9287' strokeWidth='1.08' strokeLinecap='round' />
              <path d='M5.33 1.5V4' stroke='#9C9287' strokeWidth='1.08' strokeLinecap='round' />
              <path d='M10.67 1.5V4' stroke='#9C9287' strokeWidth='1.08' strokeLinecap='round' />
            </svg>
            <span className={styles.info_text}>
              Pick{' '}
              <strong>
                {MIN_SLOTS} to {MAX_SLOTS} one-hour windows
              </strong>
              . Your mentor confirms one.
            </span>
          </div>
          {incentive && (
            <IncentiveBanner
              incentive={incentive}
              mentorName={mentorFirstName || displayName.split(' ')[0]}
              className={styles.incentive_banner}
              variant='schedule'
              inline
            />
          )}
        </div>

        <div className={styles.modal_body}>
          <div className={styles.calendar_panel}>
            <p className={styles.section_label}>Choose a Date</p>
            <div className={styles.calendar_card}>
              <DatePicker
                inline
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                minDate={minDate}
                maxDate={maxDate}
                filterDate={(date) => availableKeys.has(makeDateKey(date))}
                dayClassName={(date) => (selectedDayKeys.has(makeDateKey(date)) ? styles.day_has_selection : null)}
              />
            </div>
          </div>

          <div className={styles.slots_panel}>
            <div className={styles.slots_header}>
              <span className={styles.date_label}>
                {activeDay ? `${activeDay.dayName}, ${activeDay.dateStr}` : 'Select a date'}
              </span>
              {tzLabel && <span className={styles.tz_label}>{tzLabel}</span>}
            </div>

            <div className={styles.progress_row}>
              <div className={styles.progress_bar}>
                {Array.from({ length: MAX_SLOTS }).map((_, i) => (
                  <span
                    key={i}
                    className={`${styles.progress_segment} ${i < selectedCount ? styles.progress_segment_filled : ''}`}
                  />
                ))}
              </div>
              <span className={styles.count_badge}>
                {selectedCount}/{MIN_SLOTS}
              </span>
            </div>

            <div className={styles.slots_card}>
              {grouped ? (
                <>
                  {renderPeriodSection('Morning', grouped.morning)}
                  {renderPeriodSection('Afternoon', grouped.afternoon)}
                  {renderPeriodSection('Evening', grouped.evening)}
                  {!grouped.morning.length && !grouped.afternoon.length && !grouped.evening.length && (
                    <p className={styles.slots_empty_text}>No times available on this day.</p>
                  )}
                </>
              ) : (
                <p className={styles.slots_empty_text}>Select a date to see available times.</p>
              )}
            </div>
          </div>
        </div>

        <div className={styles.modal_footer}>
          <button
            type='button'
            className={styles.submit_btn}
            onClick={handleSubmit}
            disabled={selectedCount < MIN_SLOTS || loading}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
